'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';
import { Globe, GraduationCap, Star, TrendingUp } from 'lucide-react';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

export default function Slide20Strategic({ id, registerRef }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0f2318]">
      <div className="absolute inset-0">
        <img
          src="https://img.rocket.new/generatedImages/rocket_gen_img_17b3b3927-1777290548768.png"
          alt="Rừng xanh bạt ngàn nhìn từ trên cao, biểu tượng của phát triển bền vững và bảo vệ thiên nhiên"
          className="w-full h-full object-cover opacity-8" />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0f2318] to-[#1a3a2a]/40" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(232,160,48,0.3)] to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[rgba(76,175,125,0.03)] blur-3xl" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center">
        <div className="fade-in-up mb-4">
          <div className="chapter-badge amber">Giá trị chiến lược</div>
        </div>

        <div className="fade-in-up mb-3">
          <h2 className="font-display font-800 text-[clamp(28px,4.5vw,56px)] leading-[1.1] text-[#f0ede6] tracking-tight">
            Vì sao đây là
          </h2>
          <h2 className="font-display font-800 text-[clamp(28px,4.5vw,56px)] leading-[1.1] tracking-tight text-gradient-amber">
            quyết định chiến lược?
          </h2>
        </div>

        <div className="fade-in-up mb-12">
          <div className="w-[60px] h-[3px] bg-gradient-to-r from-[#e8a030] to-[#f5c96a] rounded-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 fade-in-up">
          {[
          {
            icon: <Globe size={28} className="text-[#4caf7d]" />,
            title: 'CSR — Trách nhiệm xã hội',
            desc: 'Chương trình gắn liền với bảo vệ môi trường — một trong những ưu tiên CSR hàng đầu của các tổ chức giáo dục hiện đại',
            points: ['Đóng góp thực tế cho môi trường', 'Gắn kết với các mục tiêu phát triển bền vững', 'Tăng uy tín và hình ảnh xã hội'],
            color: '#4caf7d'
          },
          {
            icon: <GraduationCap size={28} className="text-[#e8a030]" />,
            title: 'Phát triển sinh viên toàn diện',
            desc: 'Không chỉ học thuật — mà là phát triển con người. Đây là điều các nhà tuyển dụng và xã hội đang kỳ vọng từ các trường đại học',
            points: ['Kỹ năng mềm được rèn luyện thực tế', 'Nhận thức xã hội và trách nhiệm cộng đồng', 'Sẵn sàng cho thị trường lao động'],
            color: '#e8a030'
          },
          {
            icon: <Star size={28} className="text-[#a8d87a]" />,
            title: 'Hình ảnh tích cực',
            desc: 'Nội dung từ chương trình tạo ra làn sóng truyền thông tự nhiên — sinh viên chia sẻ, lan tỏa, và tạo ra hình ảnh đẹp cho nhà trường',
            points: ['Viral tự nhiên trên mạng xã hội', 'Nội dung chân thực hơn quảng cáo', 'Thu hút sinh viên mới thông qua câu chuyện thật'],
            color: '#a8d87a'
          },
          {
            icon: <TrendingUp size={28} className="text-[#f5c96a]" />,
            title: 'Đầu tư có ROI rõ ràng',
            desc: 'Mỗi đồng đầu tư vào chương trình tạo ra giá trị đo lường được — không phải chi phí, mà là đầu tư chiến lược',
            points: ['Chi phí thấp, giá trị cao', 'Kết quả có thể báo cáo và đo lường', 'Nền tảng cho quan hệ đối tác dài hạn'],
            color: '#f5c96a'
          }].
          map((item, i) =>
          <div
            key={`strategic-${i}`}
            className="rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1"
            style={{
              background: `linear-gradient(145deg, ${item.color}10 0%, ${item.color}03 100%)`,
              border: `1px solid ${item.color}22`
            }}>

              <div className="flex items-center gap-3 mb-4">
                <div
                className="p-2.5 rounded-xl"
                style={{ background: `${item.color}18` }}>

                  {item.icon}
                </div>
                <h3 className="font-display font-700 text-[16px] lg:text-[17px] text-[#f0ede6]">{item.title}</h3>
              </div>
              <p className="font-body text-[13px] text-[#a0b8a8] leading-relaxed mb-4">{item.desc}</p>
              <ul className="space-y-2">
                {item.points.map((pt) =>
              <li key={`strategic-pt-${pt}`} className="flex items-start gap-2">
                    <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                  style={{ background: item.color }} />

                    <span className="font-body text-[12px] lg:text-[13px] text-[#a0b8a8]">{pt}</span>
                  </li>
              )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </SlideWrapper>);

}